import React from "react";
import { translate } from "../../vendors/react-translate/ReactTranslate";
import "../../assets/stylesheets/otp-prompt-modal.css";
import {
  continueLogin,
  firebaseOneTimePassword,
} from "../../services/firebase";
import { validateEmail, validatePhone } from "../../services/ApiService";
import ApiService from "../../services/ApiService";
import PhoneInputWrapper from "../otpComponents/PhoneInputWrapper";
import EmailInputWrapper from "../otpComponents/EmailInputWrapper";
import VerifyOtpInputWrapper from "../otpComponents/VerifyOtpInputWrapper";
import SendOtpButton from "../otpComponents/SendOtpButton";
import ResendOtpSection from "../otpComponents/ResendOtpSection";
import ResendWrapper from "../otpComponents/ResendWrapper";
import { DEFAULT_COUNTRY_CODE, OTP_EVENTS } from "../../assets/appConstants";
import mixPanelService from "../../services/mixPanel/mixPanelService";

class OtpPromptModal extends React.Component {
  constructor(props) {
    super(props);
    const currentEnv = window.globalConfigTools?.currentEnvironment;
    const defaultCountryCode = this.props.countryCode || DEFAULT_COUNTRY_CODE;
    
    this.state = {
      otp: "",
      isValid: true,
      errorMessage: "",
      phoneOrEmail: "",
      step: 1,
      resendTimer: 30,
      method: this.props.isPatientMobile ? "phone" : "email",
      countryCode: defaultCountryCode,
      countryIsoCode: currentEnv === 'prod_usa' ? 'us' : 'il',
      isEmailSent: false,
      resendDisabled: false,
      otpTrials: 0,
      emailResendTrials: 0
    };
    this.handleKeyPress = this.handleKeyPress.bind(this);
    this.verifyOtp = this.verifyOtp.bind(this);
    this.sendOtp = this.sendOtp.bind(this);
    this.resendOtp = this.resendOtp.bind(this);
    this.resendEmail = this.resendEmail.bind(this);
    this.reCaptchaSmsDefenceSendOtp = this.reCaptchaSmsDefenceSendOtp.bind(this);

    this.reCaptchaSmsDefenceInit();
  }

  componentDidMount() {
    document.addEventListener("keydown", this.handleKeyPress);
  }

  componentWillUnmount() {
    document.removeEventListener("keydown", this.handleKeyPress);
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }

  handleKeyPress(e) {
    if (e.key === "Enter") {
      if (this.state.step === 1) {
        this.reCaptchaSmsDefenceSendOtp();
      } else {
        this.verifyOtp();
      }
    }
  }

  verifyOtp() {
    const { confirmationResult } = window;
    const { t } = this.props;

    if (confirmationResult && this.state.otp) {
      confirmationResult
        .confirm(this.state.otp)
        .then((result) => {
          continueLogin(result);
          this.props.onClose();
          //log it
          ApiService.logOtpEvent(OTP_EVENTS.SMS_VERIFY_SUCCESS);
          mixPanelService.trackLoginSuccess('OTP')
        })
        .catch((error) => {
          this.setState({
            isValid: false,
            errorMessage: t("login.otp.verification.failed"),
          });
          //log it
          ApiService.logOtpEvent(OTP_EVENTS.SMS_VERIFY_ERROR, { error });
          mixPanelService.trackLoginFailure('OTP', error)
        });
    } else {
      this.setState({ isValid: false, errorMessage: t("login.enterOtp") });
    }
  }

  resendEmail() {
    const { t } = this.props;
    const { emailResendTrials, resendDisabled } = this.state;

    // Don't proceed if resend is disabled or max trials reached
    if (resendDisabled || emailResendTrials >= 4) {
      if (emailResendTrials >= 4) {
        this.setState({
          isValid: false,
          errorMessage: t("login.limit.exceed"),
        });
      }
      return;
    }

    // Set resend as disabled before making the API call
    this.setState({ resendDisabled: true });

    ApiService.sendOneTimeLink(this.state.phoneOrEmail)
      .then(() => {
        this.setState((prevState) => ({
          isEmailSent: true,
          emailResendTrials: prevState.emailResendTrials + 1,
          isValid: true,
          errorMessage: "",
        }));
        this.startResendTimer();
        //log it
        ApiService.logOtpEvent(OTP_EVENTS.OTL_RESEND_SUCCESS, { phoneOrEmail: this.state.phoneOrEmail });
        mixPanelService.trackResendCode({ type: "OTL", status: "success" });
      })
      .catch((error) => {
        this.setState({
          isValid: false,
          errorMessage: t("login.otl.link.failed"),
          resendDisabled: false, // Re-enable resend on error
        });
        //log it
        ApiService.logOtpEvent(OTP_EVENTS.OTL_RESEND_ERROR, { phoneOrEmail: this.state.phoneOrEmail, error });
        mixPanelService.trackResendCode({ type: "OTL", status: "failure", error: error.message });
      });
  }

  sendOtp() {
    const { method, countryCode } = this.state;
    const { t } = this.props;
    const phoneOrEmail = (this.state.phoneOrEmail || "").trim();

    if (method === "email") {
      const isValidEmail = validateEmail(phoneOrEmail);
      if (isValidEmail) {
        this.setState({ phoneOrEmail });
        ApiService.sendOneTimeLink(phoneOrEmail)
          .then(() => {
            this.setState({ step: 2, isEmailSent: true });
            this.startResendTimer()
            //log it
            ApiService.logOtpEvent(OTP_EVENTS.OTL_SEND_SUCCESS, { phoneOrEmail });
            mixPanelService.trackOTLSuccess();
          })
          .catch((error) => {
            this.setState({
              isValid: false,
              errorMessage: t("login.otl.link.failed"),
            });
            //log it
            ApiService.logOtpEvent(OTP_EVENTS.OTL_SEND_ERROR, { phoneOrEmail, error });
            mixPanelService.trackOTLFailure(error.message);
          });
      } else {
        this.setState({
          isValid: false,
          errorMessage: t("login.otl.invalid.email"),
        });
      }
    } else if (method === "phone") {
      const { countryCode: separatedCountryCode, userValue } =
        this.separatePhoneNumber({
          value: phoneOrEmail,
          data: { dialCode: countryCode },
        });
      const formattedNumber = `${separatedCountryCode}${userValue}`;
      const isValidPhone = validatePhone(formattedNumber);

      if (isValidPhone) {
        firebaseOneTimePassword(formattedNumber)
          .then((confirmationResult) => {
            window.confirmationResult = confirmationResult;
            this.setState({ step: 2 });
            this.startResendTimer();
            //log it
            ApiService.logOtpEvent(OTP_EVENTS.SMS_SEND_SUCCESS, { phoneNumber: formattedNumber });
          })
          .catch(() => {
            this.setState({
              isValid: false,
              errorMessage: t("login.otp.send.failed"),
            });
          });
      } else {
        this.setState({
          isValid: false,
          errorMessage: t("login.otp.invalid.phone"),
        });
      }
    }
  }

  separatePhoneNumber(phoneInput) {
    let { value, data } = phoneInput;
    let countryCode = data.dialCode;
    if (!countryCode.startsWith("+")) {
      countryCode = `+${countryCode}`;
    }

    const countryCodePattern = new RegExp(`^\\+?${countryCode.replace("+", "")}`);
    let userValue = value.replace(countryCodePattern, "").replace(/[^\d]/g, "");

    if (userValue.startsWith("0")) {
      userValue = userValue.substring(1);
    }

    return { countryCode, userValue };
  }

  startResendTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
    
    this.setState({ resendTimer: 30, resendDisabled: true });
    
    let lastUpdate = Date.now();
    this.timerInterval = setInterval(() => {
      const now = Date.now();
      const delta = Math.floor((now - lastUpdate) / 1000);
      
      if (delta >= 1) {
        this.setState((prevState) => {
          const newTimer = prevState.resendTimer - 1;
          if (newTimer <= 0) {
            clearInterval(this.timerInterval);
            return { resendTimer: 0, resendDisabled: false };
          }
          return { resendTimer: newTimer };
        });
        lastUpdate = now;
      }
    }, 100);
  }

  resendOtp() {
    const { t } = this.props;
    const { otpTrials, method } = this.state;

   if (method === "phone") {
      if (otpTrials >= 2) {
        this.setState({
          isValid: false,
          errorMessage: t("login.limit.exceed"),
        });
        return;
      }

      try {
        this.reCaptchaSmsDefenceSendOtp();
        this.setState((prevState) => ({
          otpTrials: prevState.otpTrials + 1,
        }));
        ApiService.logOtpEvent(OTP_EVENTS.SMS_RESEND_SUCCESS);
        mixPanelService.trackResendCode({ type: "OTP", status: "success" });
      } catch (error) {
        ApiService.logOtpEvent(OTP_EVENTS.SMS_RESEND_ERROR, { error });
        mixPanelService.trackResendCode({ type: "OTP", status: "failure", error: error.message });
      }
    }
  }

  reCaptchaSmsDefenceGetConfig() {
    const isEmpty = (obj) => {
      for (const prop in obj) {
        if (Object.hasOwn(obj, prop)) {
          return false;
        }
      }
      return true;
    }

    const reCaptchaSmsDefenceConfig = global.globalConfigTools.serviceDiscovery('reCaptchaEnterprise');

    if (!isEmpty(reCaptchaSmsDefenceConfig) && reCaptchaSmsDefenceConfig.siteKey){
      return reCaptchaSmsDefenceConfig;
    }

    return null;
  }

  reCaptchaSmsDefenceInit() {
    const reCaptchaSmsDefenceConfig = this.reCaptchaSmsDefenceGetConfig();

    if (reCaptchaSmsDefenceConfig){
      window.wizecareHelpers.injectScript(`https://www.google.com/recaptcha/enterprise.js?render=${reCaptchaSmsDefenceConfig.siteKey}`, 'head');
    }

  }

  reCaptchaSmsDefenceSendOtp() {
    const { t } = this.props;
    const reCaptchaSmsDefenceConfig = this.reCaptchaSmsDefenceGetConfig();

    if (reCaptchaSmsDefenceConfig){
      const action = 'LOGIN';
      global.grecaptcha.enterprise.ready(async () => {
        const token = await global.grecaptcha.enterprise.execute(reCaptchaSmsDefenceConfig.siteKey, {action});
        try {
          const response = await ApiService.reCaptchaSmsDefenceCreateAssessment(reCaptchaSmsDefenceConfig, token, action);

          if (response?.data?.score <= reCaptchaSmsDefenceConfig.scoreMax) {
            this.sendOtp();
          } else {
            this.setState({
              isValid: false,
              errorMessage: t("login.otp.score.failed"),
            });

            const { phoneOrEmail } = this.state;
            ApiService.logOtpEvent(OTP_EVENTS.OTL_SCORE_ERROR, { phoneOrEmail, score: response?.data?.score });
            mixPanelService.trackOTLFailure(`${OTP_EVENTS.OTL_SCORE_ERROR}`);
          }
        } catch (error) {
          this.setState({
            isValid: false,
            errorMessage: t("login.otp.send.failed"),
          });

          const { phoneOrEmail } = this.state;
          ApiService.logOtpEvent(OTP_EVENTS.OTL_SCORE_ERROR, { phoneOrEmail, error: error.message });
          mixPanelService.trackOTLFailure(error.message);
        }
      });
    } else {
      this.sendOtp();
    }
  }

  onInputChange = (value, data) => {
    this.setState({
      phoneOrEmail: value,
      countryCode: data.dialCode,
      countryIsoCode: data.countryCode || this.state.countryIsoCode,
      isValid: true,
      isEmailSent: false,
      errorMessage: "",
    });
  };

  onMethodChange = (event) => {
    this.setState({
      method: event,
      phoneOrEmail: "",
      step: 1,
      isValid: true,
      isEmailSent: false,
      errorMessage: "",
      otp: "",
      resendTimer: 30,
      resendDisabled: false,
      otpTrials: 0,
    });
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  };

  redirectToPatientLogin = () => {
    const url = new URL(window.location.href);
    url.searchParams.set("host", "website");
    url.searchParams.set("apptype", "patient");
    window.location.href = url.toString();
  };

  render() {
    const { t, language, isPatientMobile, isWebLogin } = this.props;
    const {
      isValid,
      errorMessage,
      phoneOrEmail,
      step,
      resendTimer,
      method,
      isEmailSent,
      resendDisabled,
      countryCode,
    } = this.state;
    const isPhoneOrEmailEmpty = phoneOrEmail.trim() === "";
    const isOtpEmpty = this.state.otp.trim() === "";
    const firstTimeTitle = t("login.otp.first.time.title");
    const firstTimeDescription = t("login.otp.first.time.description");
    const resolvedFirstTimeTitle =
      firstTimeTitle === "login.otp.first.time.title" ? "First time here?" : firstTimeTitle;
    const resolvedFirstTimeDescription =
      firstTimeDescription === "login.otp.first.time.description"
        ? "Use the phone number or email that received your invitation."
        : firstTimeDescription;
    const isClinicianOtpFlow = !isPatientMobile;

    return (
      <div className="otp-prompt-top">
        <div className="otp-prompt-blur"></div>
        <div className={`prompt-content lang-${language}`}>
          {step === 1 && (
            <>
              <div className="otp-input-caption">
                {method === "phone" && !isClinicianOtpFlow
                  ? t("login.otp.phone.input.label")
                  : t("login.otp.email.input.label")}
              </div>
              <div className="input-wrapper">
                {method === "phone" && !isClinicianOtpFlow ? (
                  <PhoneInputWrapper
                    countryCode={this.state.countryIsoCode || this.state.countryCode}
                    phoneOrEmail={phoneOrEmail}
                    onInputChange={this.onInputChange}
                    placeholder={t("login.otp.phone.placeholder")}
                  />
                ) : (
                  <EmailInputWrapper
                    phoneOrEmail={phoneOrEmail}
                    setPhoneOrEmail={(value) =>
                      this.setState({
                        phoneOrEmail: value,
                        isValid: true,
                        errorMessage: "",
                      })
                    }
                    isValid={isValid}
                    t={t}
                  />
                )}
              </div>
              {!isClinicianOtpFlow && (
                <button
                  type="button"
                  className="method-switch-link"
                  onClick={() => this.onMethodChange(method === "phone" ? "email" : "phone")}
                >
                  {method === "phone"
                    ? t("login.otp.switch.to.email")
                    : t("login.otp.switch.to.phone")}
                </button>
              )}
            </>
          )}
          {!isValid && <div className="error-message">{errorMessage}</div>}
          {step === 2 && (
            <>
              {method === "email" ? (
                <ResendWrapper phoneOrEmail={phoneOrEmail} t={t} />
              ) : (
                <VerifyOtpInputWrapper
                  otp={this.state.otp}
                  setOtp={(otp) => this.setState({ otp })}
                />
              )}
            </>
          )}
          {!(method === "email" && isEmailSent) && (
            <SendOtpButton
              step={step}
              isPhoneOrEmailEmpty={isPhoneOrEmailEmpty}
              isOtpEmpty={isOtpEmpty}
              sendOtp={this.reCaptchaSmsDefenceSendOtp}
              verifyOtp={this.verifyOtp}
              t={t}
            />
          )}
          {step === 1 && (
            <div className="otp-step-supporting-text">
              <div className="code-timeout-helper">{t("login.otp.code.timeout")}</div>
              {isClinicianOtpFlow ? (
                isWebLogin ? (
                  <div className="otp-sso-hint">
                    <button
                      type="button"
                      className="patient-redirect"
                      onClick={this.redirectToPatientLogin}
                    >
                      {t("login.otp.patient.redirect.prefix")}{" "}
                      <span className="patient-redirect-cta">
                        {t("login.otp.patient.redirect.cta")}
                      </span>
                    </button>
                  </div>
                ) : null
              ) : (
                <>
                  <div className="first-time-helper">
                    <div className="first-time-helper-title">{resolvedFirstTimeTitle}</div>
                    <div className="first-time-helper-description">{resolvedFirstTimeDescription}</div>
                  </div>
                </>
              )}
            </div>
          )}
          {step === 2 && method === "phone" && (
            <ResendOtpSection
              resendTimer={resendTimer}
              resendOtp={this.resendOtp}
              resendDisabled={resendDisabled}
              t={t}
            />
          )}
            {step === 2 && method === "email" && (
            <ResendOtpSection
              resendTimer={resendTimer}
              resendOtp={this.resendEmail}
              resendDisabled={resendDisabled}
              t={t}
            />
          )}
        </div>
      </div>
    );
  }
}

export default translate("login")(OtpPromptModal);
